import { format } from 'date-fns';
import { buildDataTableCols, type DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { buildInputTableCols } from '@/components/custom/erp/input-table-v2';
import { formatMoney, formatRoundedNumber } from '@/lib/formatUtils';

export const getSupplierReturnReceiptColumns = (handleOpenViewForm: (row: MuaHangRecord) => void): DataTableColumn[] =>
  buildDataTableCols<MuaHangRecord>([
    {
      field: 'status',
      headerName: 'Trạng thái',
      width: 120,
      valueGetter: row => row?.status,
      valueFormatter: value => {
        switch (String(value ?? '')) {
          case '0':
            return 'Chưa ghi sổ';
          case '3':
            return 'Chờ duyệt';
          case '5':
            return 'Đã ghi sổ';
          case '9':
            return 'Hủy';
          default:
            return '';
        }
      },
      filter: {
        type: 'multiple',
        options: [
          { value: '0', label: 'Chưa ghi sổ' },
          { value: '3', label: 'Chờ duyệt' },
          { value: '5', label: 'Đã ghi sổ' },
          { value: '9', label: 'Hủy' }
        ]
      }
    },
    {
      field: 'ma_tthddt',
      headerName: 'Trạng thái HĐĐT',
      width: 150,
      valueGetter: row => row?.ma_tthddt,
      valueFormatter: value => {
        switch (String(value ?? '')) {
          case '0':
            return 'Không sử dụng';
          case '1':
            return 'Chờ phát hành';
          default:
            return '';
        }
      }
    },
    {
      field: 'so_ct',
      headerName: 'Số c/từ',
      width: 160,
      renderCell: params => (
        <button
          type='button'
          className='cursor-pointer bg-transparent p-0 text-left text-main hover:underline'
          onClick={event => {
            event.stopPropagation();
            handleOpenViewForm(params.row);
          }}
        >
          {params.row.so_ct}
        </button>
      )
    },
    {
      field: 'ngay_ct',
      headerName: 'Ngày c/từ',
      width: 120,
      valueGetter: row => row?.ngay_ct,
      valueFormatter: value => (value ? format(value as MuaHangRecord, 'dd/MM/yyyy') : ''),
      filter: { type: 'date' }
    },
    {
      field: 'ma_kh',
      headerName: 'Mã khách hàng',
      width: 150,
      valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || ''
    },
    {
      field: 'ten_kh',
      headerName: 'Tên khách hàng',
      width: 200,
      valueGetter: row => row?.ma_kh_data?.customer_name || row?.ten_kh || ''
    },
    { field: 'dien_giai', headerName: 'Diễn giải', width: 200 },
    {
      field: 'tk',
      headerName: 'Tk nợ',
      width: 120,
      valueGetter: row => row?.tk_data?.code || row?.tk || ''
    },
    {
      field: 't_tt_nt',
      headerName: 'Tổng tiền',
      width: 190,
      columnType: 'number',
      align: 'right',
      valueGetter: row => row?.t_tt_nt,
      valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
      filter: { type: 'text' }
    },
    {
      field: 'ma_nt',
      headerName: 'Ngoại tệ',
      width: 100,
      align: 'center',
      valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || ''
    }
  ]);

export const getSupplierReturnReceiptDetailColumns: DataTableColumn[] = buildInputTableCols<MuaHangRecord>([
  {
    field: 'tk_co',
    headerName: 'Tk có',
    width: 120,
    renderCell: params => params.row.tk_co_data?.code
  },
  {
    field: 'ten_tk_co',
    headerName: 'Tên tài khoản',
    width: 200,
    renderCell: params => params.row.tk_co_data?.name
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 120,
    columnType: 'number',
    align: 'right',
    renderCell: params => formatMoney(params.row.so_luong)
  },
  {
    field: 'gia_nt',
    headerName: 'Giá VND',
    width: 140,
    columnType: 'number',
    align: 'right',
    renderCell: params => formatMoney(params.row.gia_nt)
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 180,
    columnType: 'number',
    align: 'right',
    renderCell: params => formatMoney(params.row.tien_nt)
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => params.row.ma_vv_data?.ma_vu_viec
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: params => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: params => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: params => params.row.ma_lsx_data?.ma_lsx
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: params => params.row.ma_cp0_data?.ma_cpkhl
  }
]);

export const historyColumns: DataTableColumn[] = buildDataTableCols<MuaHangRecord>([
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
]);
